import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Zap, Rocket, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { applyFlashCredit, getFlashCredits } from "@/lib/promo";
import { errorMessage, publicAssetUrl } from "@/lib/api";
import { formatDate } from "@/lib/format";

/**
 * Small header pill: "⚡ N Flash Credits" — links to /merchant/offers.
 */
export function FlashCreditsPill() {
    const [credits, setCredits] = useState(null);

    useEffect(() => {
        getFlashCredits().then(setCredits).catch(() => setCredits(null));
    }, []);

    if (credits == null) return null;
    if (credits.remaining <= 0) return null;

    return (
        <Link
            to="/merchant/offers"
            className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-xs font-bold text-amber-900 hover:bg-amber-100"
            data-testid="flash-credits-pill"
        >
            <Zap size={12} className="text-amber-600" />
            {credits.remaining} Flash Credit{credits.remaining !== 1 ? "s" : ""}
        </Link>
    );
}

/**
 * Active-boosts card block for /merchant/home.
 */
export function ActiveBoostsSection({ credits, onChange }) {
    if (!credits) return null;
    const { active_boosts = [], remaining = 0 } = credits;
    if (remaining === 0 && active_boosts.length === 0) return null;

    return (
        <Card
            className="mt-4 rounded-2xl border border-neutral-200 p-4"
            data-testid="active-boosts-section"
        >
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
                        <Zap size={16} />
                    </div>
                    <div>
                        <div className="text-sm font-bold text-neutral-900">
                            Flash Boosts
                        </div>
                        <div className="text-[11px] text-neutral-500">
                            {remaining} credit{remaining !== 1 ? "s" : ""} left ·{" "}
                            {active_boosts.length} active
                        </div>
                    </div>
                </div>
                <Link to="/merchant/offers">
                    <Button variant="ghost" size="sm" className="text-orange-600" data-testid="boosts-see-offers">
                        Boost an offer <ChevronRight size={14} className="ml-1" />
                    </Button>
                </Link>
            </div>
            {active_boosts.length > 0 && (
                <ul className="mt-3 space-y-2" data-testid="active-boosts-list">
                    {active_boosts.map((b) => (
                        <li
                            key={b.boost_id}
                            className="flex items-center gap-3 rounded-xl border border-neutral-200 bg-white p-2.5"
                            data-testid={`active-boost-${b.boost_id}`}
                        >
                            <div className="h-10 w-10 shrink-0 overflow-hidden rounded-lg bg-neutral-100">
                                {b.poster_image_url && (
                                    <img
                                        src={publicAssetUrl(b.poster_image_url)}
                                        alt=""
                                        className="h-full w-full object-cover"
                                    />
                                )}
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="truncate text-sm font-semibold text-neutral-900">
                                    {b.post_title || "Untitled"}
                                </div>
                                <div className="text-[11px] text-neutral-500">
                                    Boosted · ends {formatDate(b.expires_at)}
                                </div>
                            </div>
                            <Badge
                                variant="outline"
                                className="border-amber-300 bg-amber-100 text-[10px] font-bold text-amber-800"
                            >
                                {b.days_remaining ?? 0}d left
                            </Badge>
                        </li>
                    ))}
                </ul>
            )}
        </Card>
    );
}

/**
 * Boost button used on offer cards + offer detail page.
 * If post is already boosted, shows "Boosted • ends <date>" chip.
 */
export function BoostButton({
    post,
    onBoosted,
    size = "sm",
    className = "",
}) {
    const [credits, setCredits] = useState(null);
    const [busy, setBusy] = useState(false);
    const [open, setOpen] = useState(false);

    const loadCredits = useCallback(async () => {
        try {
            const c = await getFlashCredits();
            setCredits(c);
        } catch (_) {}
    }, []);
    useEffect(() => {
        loadCredits();
    }, [loadCredits]);

    const activeBoost = credits?.active_boosts?.find((b) => b.post_id === post.id);

    const submit = async () => {
        try {
            setBusy(true);
            const res = await applyFlashCredit(post.id);
            toast.success(`🚀 Boosted — ${res.remaining_credits} credit${res.remaining_credits !== 1 ? "s" : ""} left`);
            setOpen(false);
            await loadCredits();
            onBoosted?.(res);
        } catch (e) {
            toast.error(errorMessage(e));
        } finally {
            setBusy(false);
        }
    };

    if (activeBoost) {
        return (
            <Badge
                variant="outline"
                className={`border-amber-300 bg-amber-50 text-[11px] font-semibold text-amber-800 ${className}`}
                data-testid={`boost-active-chip-${post.id}`}
            >
                <Zap size={11} className="mr-1 text-amber-600" /> Boosted · ends{" "}
                {formatDate(activeBoost.expires_at)}
            </Badge>
        );
    }

    // Not boosted yet
    if (!credits || credits.remaining <= 0) {
        return null;
    }
    if (post.status !== "PUBLISHED" || post.type !== "OFFER") {
        return null;
    }

    return (
        <AlertDialog open={open} onOpenChange={setOpen}>
            <AlertDialogTrigger asChild>
                <Button
                    size={size}
                    className={`nest-gradient text-white ${className}`}
                    data-testid={`boost-btn-${post.id}`}
                >
                    <Rocket size={14} className="mr-1" /> Boost
                </Button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="boost-confirm-dialog">
                <AlertDialogHeader>
                    <AlertDialogTitle>Boost this offer for 7 days?</AlertDialogTitle>
                    <AlertDialogDescription>
                        This will use 1 Flash Credit and place your offer at the
                        top of customer feeds for 7 days.
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <div className="mt-1 rounded-lg bg-amber-50 p-2 text-xs text-amber-800">
                    Credits after boost: {(credits.remaining || 0) - 1} left
                </div>
                <AlertDialogFooter>
                    <AlertDialogCancel data-testid="boost-cancel-btn">Cancel</AlertDialogCancel>
                    <AlertDialogAction
                        onClick={submit}
                        disabled={busy}
                        className="nest-gradient text-white"
                        data-testid="boost-confirm-btn"
                    >
                        {busy ? "Boosting…" : "Yes, boost"}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}
